import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, type SxProps, type Theme } from '@mui/material';
import { mergeSx } from 'merge-sx';

import ChevronRightIcon from '@/assets/icons/chevron-right.svg?react';
import { useOrganizationStructure } from '@/hooks/useOrganizationStructure';
import { toOrganizationItems } from '@/root/linker';
import { transforms, transitions } from '@/theme/constants';

import LocationTreeSelect, { type LocationTreeSelectDropdownIconProps } from './LocationTreeSelect';

interface LocationSelectProps {
  sx?: SxProps<Theme>;
  organizationSlug?: string;
}

const LocationTreeSelectDropdownIcon: React.FC<LocationTreeSelectDropdownIconProps> = ({ isOpen }) => (
  <Box
    sx={{
      '& .ArrowIcon-root': {
        transform: transforms.compose(isOpen && transforms.rotate90),
        transition: transitions.transform
      }
    }}
  >
    <ChevronRightIcon className="ArrowIcon-root" />
  </Box>
);

const LocationNavSelect: React.FC<LocationSelectProps> = ({ sx, organizationSlug }) => {
  const navigate = useNavigate();

  const { flatStructure: flatOrganizationStructure } = useOrganizationStructure();

  const handleChange = useCallback(
    (newValue: string[] | string) => {
      if (!organizationSlug) {
        return;
      }

      const selectedValue = newValue as string;
      if (selectedValue && flatOrganizationStructure) {
        const selectedNode = flatOrganizationStructure.find(node => node.slug === selectedValue);
        if (selectedNode) {
          navigate(
            toOrganizationItems({
              organizationSlug,
              locationSlug: selectedNode.slug
            })
          );
        }
      }
    },
    [navigate, organizationSlug, flatOrganizationStructure]
  );

  return (
    <Box
      sx={mergeSx(
        {
          '& .p-treeselect.p-component.p-inputwrapper.p-treeselect-chip': {
            border: 'none',
            width: 24,
            height: 36,
            minHeight: 'unset',
            background: 'transparent'
          },
          '& .p-treeselect-trigger': {
            width: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          },
          '& .p-treeselect-trigger .MuiBox-root': {
            height: 24
          },
          '& .ArrowIcon-root': {
            color: 'primary.500'
          }
        },
        sx
      )}
    >
      <LocationTreeSelect
        onChange={handleChange}
        isMulti={false}
        organizationSlug={organizationSlug}
        value={undefined}
        dropdownIconComponent={LocationTreeSelectDropdownIcon}
      />
    </Box>
  );
};

export default LocationNavSelect;
